import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Lock, ArrowRight, ShieldCheck, Zap, Sparkles } from "lucide-react";
import { usePlanLimitStore } from "../../store/planLimitStore";

export default function PlanLimitModal() {
  const navigate = useNavigate();
  const { isOpen, data, closeLimitModal } = usePlanLimitStore();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeLimitModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeLimitModal]);

  if (!isOpen || !data) return null;

  const handleUpgradeClick = () => {
    closeLimitModal();
    navigate("/billing");
  };

  const featureName = data.featureName || "This Feature";
  const title =
    data.title ||
    (data.limit === 0
      ? `${featureName} Not Available on Your Plan`
      : `${featureName} Quota Limit Reached`);

  const message =
    data.message ||
    `Your current organization subscription does not include ${featureName.toLowerCase()}. Upgrade your subscription plan to unlock full access and continue.`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
        {/* Header gradient banner */}
        <div className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 p-6 text-white relative">
          <button
            type="button"
            onClick={closeLimitModal}
            className="absolute top-4 right-4 p-2 rounded-xl text-indigo-200 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="h-11 w-11 rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center shrink-0">
              <Lock className="h-5 w-5 text-indigo-300" />
            </div>
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-300">
                Subscription Required
              </span>
              <h3 className="text-base font-bold text-white leading-snug">
                {title}
              </h3>
            </div>
          </div>

          <p className="text-xs text-indigo-100/90 leading-relaxed mt-1">
            {message}
          </p>
        </div>

        {/* Modal body */}
        <div className="p-6 space-y-5">
          {/* Quota details if available */}
          {data.limit !== undefined && (
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 font-medium">Plan Status:</span>
                <span className="font-bold text-slate-800">
                  {data.limit === 0 ? "Not Included (0 Allowance)" : `Quota: ${data.limit} Max`}
                </span>
              </div>
              {data.current !== undefined && data.limit > 0 && (
                <span className="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                  {data.current} Used
                </span>
              )}
            </div>
          )}

          {/* Benefits summary */}
          <div className="space-y-2.5">
            <p className="text-xs font-bold text-slate-900">
              Upgrading your plan unlocks:
            </p>
            <div className="space-y-2 text-xs text-slate-600">
              <div className="flex items-start gap-2.5">
                <div className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldCheck className="h-2.5 w-2.5" />
                </div>
                <span>Automated multi-channel payment reminders &amp; overdue debt follow-ups</span>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                  <Zap className="h-2.5 w-2.5" />
                </div>
                <span>Higher debtor enrollment quotas and unlimited recovery plans</span>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="h-2.5 w-2.5" />
                </div>
                <span>Priority SMS and email routing with higher throughput</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
            <button
              type="button"
              onClick={handleUpgradeClick}
              className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all shadow-sm shadow-indigo-600/20"
            >
              <span>Upgrade Subscription</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={closeLimitModal}
              className="w-full sm:w-auto px-4 py-2.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
